import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  ChargeMasterService,
  ClientService,
  SubscriptionService,
  ToastMessageService,
} from '@fitness-force/api';
import {
  ClientSubscriptionFreezeType,
  OnContractExpiry,
  PlanCategory,
  PlanFreezeType,
  Status,
  SubscriptionStatus,
} from '@fitness-force/enum';
import { AppErrors } from '@fitness-force/models';
import type {
  JsonPatchOperation,
  PatchableSubscription,
  SubscriptionById,
  TENANT_MODEL,
  TENANTSINTERFACE,
} from '@fitness-force/models';
import { TENANTS_INFO } from '@fitness-force/state';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { Subject, takeUntil } from 'rxjs';

interface SelectOption<T = number | string> {
  label: string;
  value: T;
}

interface ChargeMasterOption {
  id: number;
  name_primary_language: string;
  status?: string;
}

@Component({
  selector: 'app-subscription-patch',
  templateUrl: './subscription-patch.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    ToggleSwitchModule,
    RippleModule,
    SkeletonModule,
    TranslateModule,
  ],
})
export class SubscriptionPatchComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly clientService = inject(ClientService);
  private readonly chargeMasterService = inject(ChargeMasterService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();

  readonly visible = input(false);
  readonly subscriptionId = input(0);
  readonly clientId = input(0);
  readonly closeEvent = output<boolean>();

  readonly isSubmitting = signal(false);
  readonly isLoading = signal(false);
  readonly loadError = signal(false);
  readonly loadedSubscription = signal<PatchableSubscription | null>(null);
  readonly isRecurring = signal(false);
  readonly chargeMasters = signal<ChargeMasterOption[]>([]);
  readonly tenantOptions = signal<TENANT_MODEL[]>([]);

  readonly freezeTypeOptions: SelectOption<number>[] = [
    { label: 'No Freeze', value: PlanFreezeType.NoFreeze },
    { label: 'Free Freeze', value: PlanFreezeType.FreeFreeze },
    { label: 'Paid Freeze', value: PlanFreezeType.PaidFreeze },
  ];

  readonly contractExpiryOptions: SelectOption<number>[] = [
    { label: 'Contract Expires', value: OnContractExpiry.ContractExpire },
    { label: 'Auto Renew', value: OnContractExpiry.ContractAutoRenew },
    {
      label: 'Convert to Pay As You Go',
      value: OnContractExpiry.ContractConvertsToPayAsYouGo,
    },
  ];

  patchForm: FormGroup = this.fb.group({});
  private originalValues: Record<string, unknown> = {};

  constructor() {
    effect(() => {
      if (this.visible() && this.subscriptionId() > 0 && this.clientId() > 0) {
        this.loadSubscriptionDetails();
      } else if (!this.visible()) {
        this.resetState();
      }
    });
  }

  ngOnInit(): void {
    this.store
      .select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenantInfo: TENANTSINTERFACE) => {
        this.tenantOptions.set(tenantInfo?.all_tenant ?? []);
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetState(): void {
    this.loadedSubscription.set(null);
    this.isLoading.set(false);
    this.loadError.set(false);
    this.isSubmitting.set(false);
    this.patchForm = this.fb.group({});
    this.originalValues = {};
  }

  private loadSubscriptionDetails(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.loadedSubscription.set(null);
    this.changeDetectorRef.markForCheck();

    this.clientService
      .getSingleSubscriptionById(this.clientId(), this.subscriptionId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PatchableSubscription) => {
          this.loadedSubscription.set(response);
          this.isRecurring.set(this.checkIsRecurring(response));
          this.originalValues = this.buildOriginalValues(response);
          this.loadChargeMastersAndBuildForm(response);
        },
        error: (error: AppErrors) => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.toastMessageService.showError(error);
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private loadChargeMastersAndBuildForm(
    subscription: PatchableSubscription,
  ): void {
    const selectedChargeMasterId =
      this.resolveEarlyCancellationChargeMasterId(subscription);

    this.chargeMasterService
      .getChargeMasters(subscription.tenant_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: unknown) => {
          const parsed = this.parseChargeMasters(
            response,
            selectedChargeMasterId,
          );
          this.chargeMasters.set(
            this.ensureSelectedChargeMasterOption(subscription, parsed),
          );
          this.buildPatchForm();
          this.isLoading.set(false);
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.chargeMasters.set([]);
          this.buildPatchForm();
          this.isLoading.set(false);
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private parseChargeMasters(
    response: unknown,
    selectedId: number | null = null,
  ): ChargeMasterOption[] {
    let items: ChargeMasterOption[] = [];

    if (Array.isArray(response)) {
      items = response as ChargeMasterOption[];
    } else if (response && typeof response === 'object') {
      const payload = response as {
        items?: ChargeMasterOption[];
        data?: ChargeMasterOption[];
      };
      items = payload.items ?? payload.data ?? [];
    }

    return items
      .filter((chargeMaster) => {
        if (chargeMaster?.id == null) {
          return false;
        }
        const isActive =
          chargeMaster.status === Status.Active.toString() ||
          chargeMaster.status === Status[Status.Active];
        return isActive || Number(chargeMaster.id) === selectedId;
      })
      .map((chargeMaster) => ({
        ...chargeMaster,
        id: Number(chargeMaster.id),
      }));
  }

  private ensureSelectedChargeMasterOption(
    subscription: PatchableSubscription,
    masters: ChargeMasterOption[],
  ): ChargeMasterOption[] {
    const selectedId = this.resolveEarlyCancellationChargeMasterId(subscription);
    if (
      selectedId == null ||
      masters.some((master) => master.id === selectedId)
    ) {
      return masters;
    }

    if (subscription.cancel_fee?.id) {
      return [
        ...masters,
        {
          id: Number(subscription.cancel_fee.id),
          name_primary_language:
            subscription.cancel_fee.name_primary_language ||
            subscription.cancel_fee.name_secondary_language ||
            `Charge #${subscription.cancel_fee.id}`,
        },
      ];
    }

    return masters;
  }

  private resolveEarlyCancellationChargeMasterId(
    subscription: PatchableSubscription,
  ): number | null {
    const rawId =
      subscription.break_chrgmst_id ??
      subscription.early_cancellation_charge_master_id ??
      subscription.cancel_fee?.id;

    if (rawId === undefined || rawId === null || rawId === 0) {
      return null;
    }

    return Number(rawId);
  }

  private buildPatchForm(): void {
    this.patchForm = this.fb.group({
      suprema_group_id: [this.originalValues['suprema_group_id'] ?? ''],
      on_contract_expiry: [this.originalValues['on_contract_expiry']],
      freeze_type: [this.originalValues['freeze_type']],
      freeze_allowed_number_of_attempts_in_year: [
        this.originalValues['freeze_allowed_number_of_attempts_in_year'] ?? 0,
      ],
      freeze_max_days_per_instance: [
        this.originalValues['freeze_max_days_per_instance'] ?? 0,
      ],
      freeze_min_days_per_instance: [
        this.originalValues['freeze_min_days_per_instance'] ?? 0,
      ],
      freeze_can_only_billing_cycle: [
        this.originalValues['freeze_can_only_billing_cycle'] ?? false,
      ],
      upgrade_allowed_days: [this.originalValues['upgrade_allowed_days'] ?? 0],
      transferable: [this.originalValues['transferable'] ?? false],
      min_days_req_for_transfer: [
        this.originalValues['min_days_req_for_transfer'] ?? 0,
      ],
      feature_is_cash_recurring: [
        this.originalValues['feature_is_cash_recurring'] ?? false,
      ],
      is_autopay_enabled: [this.originalValues['is_autopay_enabled'] ?? false],
      early_cancellation_charge_master_id: [
        this.originalValues['early_cancellation_charge_master_id'],
      ],
      feature_is_online_termination_allowed: [
        this.originalValues['feature_is_online_termination_allowed'] ?? false,
      ],
      max_concurrent_guest_pass: [
        this.originalValues['max_concurrent_guest_pass'] ?? 0,
      ],
      guest_pass_count: [this.originalValues['guest_pass_count'] ?? 0],
      allow_cross_tenant_access: [
        this.originalValues['allow_cross_tenant_access'] ?? false,
      ],
      access_tenant_in: [this.originalValues['access_tenant_in'] ?? []],
      noshow_late_cancel_limit_per_year: [
        this.originalValues['noshow_late_cancel_limit_per_year'] ?? 0,
      ],
    });

    this.changeDetectorRef.markForCheck();
  }

  private checkIsRecurring(subscription: SubscriptionById): boolean {
    return (
      subscription.subscription_category === PlanCategory.Contract ||
      subscription.subscription_category?.toString() ===
        PlanCategory[PlanCategory.Contract] ||
      subscription.subscription_category === PlanCategory.PayAsYouGo ||
      subscription.subscription_category?.toString() ===
        PlanCategory[PlanCategory.PayAsYouGo]
    );
  }

  private resolveEnumValue(
    value: string | number | undefined | null,
    enumType: Record<string, string | number>,
  ): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && enumType[numericValue] !== undefined) {
      return numericValue;
    }
    const enumKey = enumType[value as keyof typeof enumType];
    return typeof enumKey === 'number' ? enumKey : null;
  }

  private resolveFreezeType(
    value: string | number | undefined | null,
  ): number | null {
    const clientFreeze = this.resolveEnumValue(
      value,
      ClientSubscriptionFreezeType,
    );
    if (clientFreeze !== null) {
      if (clientFreeze === ClientSubscriptionFreezeType.NoFreeze) {
        return PlanFreezeType.NoFreeze;
      }
      return clientFreeze;
    }
    return this.resolveEnumValue(value, PlanFreezeType);
  }

  private parseTenantIds(accessTenantIn?: string): number[] {
    if (!accessTenantIn) {
      return [];
    }
    return accessTenantIn
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }

  private buildOriginalValues(
    subscription: PatchableSubscription,
  ): Record<string, unknown> {
    return {
      suprema_group_id: subscription.suprema_group_id ?? '',
      on_contract_expiry: this.resolveEnumValue(
        subscription.on_contract_expiry,
        OnContractExpiry,
      ),
      freeze_type: this.resolveFreezeType(subscription.freeze_type),
      freeze_allowed_number_of_attempts_in_year:
        subscription.freeze_allowed_number_of_attempts_in_year ?? 0,
      freeze_max_days_per_instance:
        subscription.freeze_maximum_days_per_instance ?? 0,
      freeze_min_days_per_instance:
        subscription.freeze_min_days_per_instance ?? 0,
      freeze_can_only_billing_cycle:
        subscription.freeze_can_only_billing_cycle ?? false,
      upgrade_allowed_days: subscription.upgrade_allowed_days ?? 0,
      transferable: subscription.transferable ?? subscription.is_transferable ?? false,
      min_days_req_for_transfer:
        subscription.minimum_days_required_for_transfer ?? 0,
      feature_is_cash_recurring: subscription.feature_is_cash_recurring ?? false,
      is_autopay_enabled: subscription.is_autopay_enabled ?? false,
      early_cancellation_charge_master_id:
        this.resolveEarlyCancellationChargeMasterId(subscription),
      feature_is_online_termination_allowed:
        subscription.feature_is_online_termination_allowed ?? false,
      max_concurrent_guest_pass: subscription.max_concurrent_guest_pass ?? 0,
      guest_pass_count: subscription.guest_pass_count ?? 0,
      allow_cross_tenant_access: subscription.allow_cross_tenant_access ?? false,
      access_tenant_in: this.parseTenantIds(subscription.access_tenant_in),
      noshow_late_cancel_limit_per_year:
        subscription.noshow_late_cancel_limit_per_year ?? 0,
    };
  }

  private valuesEqual(a: unknown, b: unknown): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }
      return a.every((value, index) => value === b[index]);
    }
    return a === b;
  }

  private buildPatchOperations(): JsonPatchOperation[] {
    const formValue = this.patchForm.getRawValue();
    const operations: JsonPatchOperation[] = [];

    const fieldMap: Array<{
      path: string;
      key: string;
      recurringOnly?: boolean;
      transform?: (value: unknown) => string | number | boolean;
    }> = [
      { path: '/SupremaGroupId', key: 'suprema_group_id' },
      {
        path: '/OnContractExpiry',
        key: 'on_contract_expiry',
        recurringOnly: true,
        transform: (value) => Number(value),
      },
      {
        path: '/FreezeType',
        key: 'freeze_type',
        transform: (value) => Number(value),
      },
      {
        path: '/FreezeAllowedNumberOfAttemptsInYear',
        key: 'freeze_allowed_number_of_attempts_in_year',
        transform: (value) => Number(value),
      },
      {
        path: '/FreezeMaxDaysPerInstance',
        key: 'freeze_max_days_per_instance',
        transform: (value) => Number(value),
      },
      {
        path: '/FreezeMinDaysPerInstance',
        key: 'freeze_min_days_per_instance',
        transform: (value) => Number(value),
      },
      {
        path: '/FreezeCanOnlyBillingCycle',
        key: 'freeze_can_only_billing_cycle',
        recurringOnly: true,
        transform: (value) => Boolean(value),
      },
      {
        path: '/UpgradeAllowedDays',
        key: 'upgrade_allowed_days',
        transform: (value) => Number(value),
      },
      {
        path: '/Transferable',
        key: 'transferable',
        transform: (value) => Boolean(value),
      },
      {
        path: '/MinDaysReqForTransfer',
        key: 'min_days_req_for_transfer',
        transform: (value) => Number(value),
      },
      {
        path: '/FeatureIsCashRecurring',
        key: 'feature_is_cash_recurring',
        recurringOnly: true,
        transform: (value) => Boolean(value),
      },
      {
        path: '/IsAutopayEnabled',
        key: 'is_autopay_enabled',
        recurringOnly: true,
        transform: (value) => Boolean(value),
      },
      {
        path: '/BreakChrgmstId',
        key: 'early_cancellation_charge_master_id',
        recurringOnly: true,
        transform: (value) => Number(value),
      },
      {
        path: '/FeatureIsOnlineTerminationAllowed',
        key: 'feature_is_online_termination_allowed',
        recurringOnly: true,
        transform: (value) => Boolean(value),
      },
      {
        path: '/MaxConcurrentGuestPass',
        key: 'max_concurrent_guest_pass',
        transform: (value) => Number(value),
      },
      {
        path: '/GuestPassCount',
        key: 'guest_pass_count',
        transform: (value) => Number(value),
      },
      {
        path: '/AllowCrossTenantAccess',
        key: 'allow_cross_tenant_access',
        transform: (value) => Boolean(value),
      },
      {
        path: '/AccessTenantIn',
        key: 'access_tenant_in',
        transform: (value) =>
          Array.isArray(value) ? (value as number[]).join(',') : String(value ?? ''),
      },
      {
        path: '/NoShowLateCancelLimitPerYear',
        key: 'noshow_late_cancel_limit_per_year',
        transform: (value) => Number(value),
      },
    ];

    for (const field of fieldMap) {
      if (field.recurringOnly && !this.isRecurring()) {
        continue;
      }

      const newValue = field.transform
        ? field.transform(formValue[field.key])
        : formValue[field.key];
      const originalValue = field.transform
        ? field.transform(this.originalValues[field.key])
        : this.originalValues[field.key];

      if (!this.valuesEqual(newValue, originalValue)) {
        operations.push({
          op: 'replace',
          path: field.path,
          value: newValue as string | number | boolean,
        });
      }
    }

    return operations;
  }

  submitPatch(): void {
    if (this.patchForm.invalid) {
      this.patchForm.markAllAsTouched();
      this.changeDetectorRef.markForCheck();
      return;
    }

    const patchOperations = this.buildPatchOperations();
    if (patchOperations.length === 0) {
      this.translate
        .get('SUBSCRIPTIONS.PATCH.NO_CHANGES')
        .subscribe((message) => {
          this.toastMessageService.showWarning(message);
        });
      return;
    }

    const subscription = this.loadedSubscription();
    if (!subscription) {
      return;
    }

    this.isSubmitting.set(true);
    this.subscriptionService
      .patchSubscription(this.clientId(), subscription.id, patchOperations)
      .subscribe({
        next: () => {
          this.translate
            .get('SUBSCRIPTIONS.PATCH.SUCCESS_MSG')
            .subscribe((message) => {
              this.toastMessageService.showSuccess(message);
            });
          this.isSubmitting.set(false);
          this.closeEvent.emit(true);
          this.changeDetectorRef.markForCheck();
        },
        error: (error: AppErrors) => {
          this.isSubmitting.set(false);
          this.toastMessageService.showError(error);
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  closeDialog(): void {
    this.resetState();
    this.closeEvent.emit(false);
  }

  static canPatchSubscription(status: string): boolean {
    const subscriptionStatus =
      SubscriptionStatus[status as keyof typeof SubscriptionStatus];
    return ![
      SubscriptionStatus.Void,
      SubscriptionStatus.Cancelled,
      SubscriptionStatus.CancelledDueToNonPayment,
      SubscriptionStatus.Expired,
    ].includes(subscriptionStatus);
  }
}
